import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card } from '@diwata/ironvale'
import { useUI } from '@/store/ui'
import { GraphAPI, type GraphNode } from '@/api/graph'
import { TroveAPI, type BulkProposal, type ImportResult, type TroveDoc, type UpdateResult } from '@/api/trove'
import { filterImportable, groupByTopDir } from '@/lib/importRules'
import { renderAnswer } from '@/lib/citations'
import { useEntityPeek } from '@/components/EntityPeek'
import { usePrefill } from '@/store/prefill'
import { HelpHint } from '@/components/HelpHint'

// Trove = your files become stars. Import notes (Obsidian [[links]] resolve against the
// graph), capture thoughts directly, read everything back with live citations — and take
// the whole graph out as JSON whenever you want. Unknown names ask in the ◈ inbox; the
// graph never silently absorbs a guess.
// Named Trove, not Vault: "Vault" is the bronze artifact store in the data layer.

export function Trove() {
  const { diwaUrl } = useUI()
  const api = useMemo(() => new TroveAPI(diwaUrl), [diwaUrl])
  const graphApi = useMemo(() => new GraphAPI(diwaUrl), [diwaUrl])
  const qc = useQueryClient()
  const { openPeek } = useEntityPeek()
  const fileInput = useRef<HTMLInputElement>(null)

  const { data: docs, isLoading: docsLoading, isError: docsError } = useQuery({ queryKey: ['trove'], queryFn: () => api.list() })
  const { data: network } = useQuery({
    queryKey: ['graph-network', 'all'],
    queryFn: () => graphApi.network('all'),
  })
  const byName = useMemo(() => {
    const m = new Map<string, GraphNode>()
    for (const n of network?.nodes ?? []) m.set(n.name.toLowerCase(), n)
    return m
  }, [network])

  const [capture, setCapture] = useState('')
  const consumeNoteCapture = usePrefill((st) => st.consumeNoteCapture)
  useEffect(() => {
    const seed = consumeNoteCapture()
    if (seed) setCapture(seed)
  }, [consumeNoteCapture])
  const [busy, setBusy] = useState(false)
  const [receipts, setReceipts] = useState<ImportResult[]>([])
  const [reading, setReading] = useState<{ doc: TroveDoc; content: string } | null>(null)
  // The editor: only Diwa-born notes open it. Imported files mirror the vault and stay
  // read-only until desktop write-back can edit the file itself.
  const [draft, setDraft] = useState<string | null>(null)
  const [editReceipt, setEditReceipt] = useState<UpdateResult | null>(null)

  // Folder import: scan → prefilter → proposal → HUMAN GATE → batched import.
  const folderInput = useRef<HTMLInputElement>(null)
  const [gate, setGate] = useState<{
    files: Map<string, File>
    proposal: BulkProposal
    checked: Set<string>
    scanned: number
    prefilterDropped: number
  } | null>(null)
  const [bulkRun, setBulkRun] = useState<{
    done: number
    total: number
    imported: number
    deduped: number
    suggested: number
    failed: number
    halted: boolean
  } | null>(null)
  const cancelRef = useRef(false)

  async function pickFolder(list: FileList | null) {
    if (!list || busy) return
    setBusy(true)
    try {
      const files = new Map<string, File>()
      for (const file of Array.from(list)) {
        const path = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name
        files.set(path, file)
      }
      const manifest = [...files.entries()].map(([path, file]) => ({ path, size: file.size }))
      const { keep, ignored } = filterImportable(manifest)
      const proposal = await api.bulkPropose(keep)
      setGate({
        files,
        proposal,
        checked: new Set(proposal.selected),
        scanned: manifest.length,
        prefilterDropped: ignored,
      })
      setBulkRun(null)
    } finally {
      setBusy(false)
      if (folderInput.current) folderInput.current.value = ''
    }
  }

  async function runBulkImport() {
    if (!gate || busy) return
    const paths = gate.proposal.selected.filter((p) => gate.checked.has(p))
    if (paths.length === 0) return
    setBusy(true)
    cancelRef.current = false
    const run = {
      done: 0, total: paths.length, imported: 0, deduped: 0, suggested: 0,
      failed: 0, halted: false,
    }
    setBulkRun({ ...run })
    try {
      const BATCH = 8
      for (let i = 0; i < paths.length && !cancelRef.current; i += BATCH) {
        const chunk = paths.slice(i, i + BATCH)
        await Promise.all(
          chunk.map(async (path) => {
            // One bad file must never kill the run — it counts as failed and the
            // rest keep flowing.
            try {
              const file = gate.files.get(path)
              if (!file) return
              const content = await file.text()
              if (!content.trim()) return
              const r = await api.importNote(content, { filename: path, source: 'obsidian' })
              if (r.deduped) run.deduped += 1
              else run.imported += 1
              run.suggested += r.suggested
            } catch {
              run.failed += 1
            }
          }),
        )
        run.done = Math.min(i + BATCH, paths.length)
        setBulkRun({ ...run })
      }
      run.halted = cancelRef.current
      setBulkRun({ ...run })
      // A stopped run keeps its gate so the user can resume — dedupe makes the
      // re-run converge on what's left.
      if (!run.halted) setGate(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function refresh() {
    await qc.invalidateQueries({ queryKey: ['trove'] })
    await qc.invalidateQueries({ queryKey: ['graph-network', 'all'] })
    await qc.invalidateQueries({ queryKey: ['suggestions', 'open'] })
  }

  async function importFiles(files: FileList | null) {
    if (!files || busy) return
    setBusy(true)
    const results: ImportResult[] = []
    try {
      for (const file of Array.from(files)) {
        const content = await file.text()
        if (!content.trim()) continue
        const r = await api.importNote(content, { filename: file.name, source: 'obsidian' })
        results.push(r)
      }
      setReceipts(results)
      await refresh()
    } finally {
      setBusy(false)
      if (fileInput.current) fileInput.current.value = ''
    }
  }

  async function captureNote() {
    if (!capture.trim() || busy) return
    setBusy(true)
    try {
      const r = await api.importNote(capture, { source: 'capture' })
      setReceipts([r])
      setCapture('')
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function open(doc: TroveDoc) {
    const detail = await api.detail(doc.id)
    setReading({ doc, content: detail.content })
    setDraft(null)
    setEditReceipt(null)
  }

  async function saveEdit() {
    if (!reading || draft === null || busy) return
    setBusy(true)
    try {
      const r = await api.update(reading.doc.id, draft)
      setEditReceipt(r.unchanged ? null : r)
      setReading({
        doc: { ...reading.doc, title: r.title ?? reading.doc.title },
        content: draft,
      })
      setDraft(null)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  // A cited entity opens the peek anchored to the citation — a quick look with an explicit
  // route into the Constellation (the peek's "Open" both selects and navigates).
  function pickNode(n: GraphNode, anchor: HTMLElement) {
    openPeek(n, anchor)
  }

  function downloadGraph() {
    const payload = network ?? { nodes: [], edges: [] }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'diwa-graph.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      <div className="dw-page-header">
        <div>
          <div className="dw-page-title">Trove <HelpHint term="trove" /></div>
          <div className="dw-page-subtitle">
            Your files become <HelpHint term="star">stars</HelpHint> — notes import, citations link,
            unknowns ask. No lock-in.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={fileInput}
            type="file"
            accept=".md,.txt,.markdown"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => importFiles(e.target.files)}
          />
          <input
            ref={folderInput}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => pickFolder(e.target.files)}
            {...({ webkitdirectory: '' } as object)}
          />
          <Button variant="primary" size="sm" disabled={busy} onClick={() => fileInput.current?.click()}>
            {busy ? 'Importing…' : '⬆ Import notes'}
          </Button>
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => folderInput.current?.click()}>
            ⬆ Import folder
          </Button>
          <Button variant="ghost" size="sm" onClick={downloadGraph}>
            Download graph
          </Button>
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <div className="dw-rail-section-title">Quick capture</div>
        <div style={{ display: 'grid', gap: 8 }}>
          <textarea
            className="dw-input dw-textarea"
            rows={3}
            placeholder="Write a note — [[Entity Names]] link straight into your constellation."
            value={capture}
            onChange={(e) => setCapture(e.target.value)}
          />
          <div>
            <Button variant="primary" size="sm" disabled={busy || !capture.trim()} onClick={captureNote}>
              Capture
            </Button>
          </div>
        </div>
      </Card>

      {gate && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="dw-rail-section-title">Folder import — your gate</div>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => setGate(null)}>
              Discard
            </Button>
          </div>
          <div className="dw-inbox-detail" style={{ marginBottom: 10 }}>
            {gate.scanned} files scanned · {gate.prefilterDropped + gate.proposal.ignored} skipped as
            not-your-writing{gate.proposal.capped && ' · capped at 500'} ·{' '}
            {gate.proposal.heuristic_count !== gate.proposal.selected.length &&
              `Familiar narrowed ${gate.proposal.heuristic_count} → ${gate.proposal.selected.length} · `}
            nothing imports until you say so
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', display: 'grid', gap: 4, marginBottom: 10 }}>
            {[...groupByTopDir(gate.proposal.selected).entries()].map(([dir, paths]) => (
              <div key={dir}>
                <label style={{ fontSize: 'var(--ae-font-size-xs)', fontWeight: 600, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    checked={paths.every((p) => gate.checked.has(p))}
                    onChange={(e) => {
                      const next = new Set(gate.checked)
                      for (const p of paths) e.target.checked ? next.add(p) : next.delete(p)
                      setGate({ ...gate, checked: next })
                    }}
                  />
                  {dir} <span className="dw-inbox-detail">({paths.length})</span>
                </label>
                {paths.map((p) => (
                  <label key={p} style={{ fontSize: 'calc(11px * var(--ae-text-scale, 1))', display: 'flex', gap: 6, alignItems: 'center', marginLeft: 18, color: 'var(--ae-color-text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={gate.checked.has(p)}
                      onChange={(e) => {
                        const next = new Set(gate.checked)
                        e.target.checked ? next.add(p) : next.delete(p)
                        setGate({ ...gate, checked: next })
                      }}
                    />
                    {p}
                  </label>
                ))}
              </div>
            ))}
          </div>
          <Button
            size="sm"
            variant="primary"
            disabled={busy || [...gate.checked].length === 0}
            onClick={runBulkImport}
          >
            Import {[...gate.checked].length} notes
          </Button>
        </Card>
      )}

      {bulkRun && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div className="dw-rail-section-title">
              {bulkRun.halted
                ? 'Import stopped — the gate keeps the rest'
                : bulkRun.done < bulkRun.total
                  ? 'Importing…'
                  : 'Folder import complete'}
            </div>
            {!bulkRun.halted && bulkRun.done < bulkRun.total && (
              <Button size="sm" variant="ghost" onClick={() => { cancelRef.current = true }}>
                Stop
              </Button>
            )}
          </div>
          <div style={{ height: 6, background: 'var(--ae-color-bg-muted)', borderRadius: 3, marginBottom: 8 }}>
            <div
              style={{
                height: '100%',
                width: `${Math.round((bulkRun.done / Math.max(1, bulkRun.total)) * 100)}%`,
                background: 'var(--ae-color-accent)',
                borderRadius: 3,
                transition: 'width 200ms ease',
              }}
            />
          </div>
          <div className="dw-inbox-detail">
            {bulkRun.done}/{bulkRun.total} · {bulkRun.imported} imported · {bulkRun.deduped} already
            in the trove{bulkRun.suggested > 0 && ` · ${bulkRun.suggested} asking in ◈`}
            {bulkRun.failed > 0 && ` · ${bulkRun.failed} failed — re-run the folder to retry`}
          </div>
        </Card>
      )}

      {receipts.length > 0 && (
        <Card style={{ marginBottom: 16 }}>
          <div className="dw-rail-section-title">Import receipt</div>
          {receipts.map((r) => (
            <div key={r.note_id} style={{ fontSize: 'var(--ae-font-size-xs)', color: 'var(--ae-color-text-secondary)', marginBottom: 6 }}>
              <strong>{r.title ?? 'note'}</strong>
              {r.deduped
                ? ' — already in the trove (identical bytes)'
                : ` — linked ${r.linked.length}${r.created.length ? `, created ${r.created.join(', ')}` : ''}${r.suggested ? `, ${r.suggested} asking in ◈` : ''}`}
              {r.linked.length > 0 && (
                <span style={{ color: 'var(--ae-color-text-muted)' }}> · {r.linked.join(', ')}</span>
              )}
            </div>
          ))}
        </Card>
      )}

      {reading && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div className="dw-rail-section-title">{reading.doc.title ?? reading.doc.filename}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {reading.doc.source === 'capture' && draft === null && (
                <Button size="sm" variant="ghost" onClick={() => { setDraft(reading.content); setEditReceipt(null) }}>
                  Edit
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => { setReading(null); setDraft(null) }}>Close</Button>
            </div>
          </div>
          {reading.doc.source !== 'capture' && (
            <div className="dw-inbox-detail" style={{ marginBottom: 8 }}>
              mirrored from {reading.doc.filename ?? 'an imported file'} — read-only; edits live in your vault
            </div>
          )}
          {editReceipt && (
            <div className="dw-inbox-detail" style={{ marginBottom: 8 }}>
              saved — {editReceipt.linked.length ? `linked ${editReceipt.linked.join(', ')}` : 'no new links'}
              {editReceipt.unlinked.length > 0 && ` · unlinked ${editReceipt.unlinked.join(', ')}`}
              {editReceipt.created.length > 0 && ` · created ${editReceipt.created.join(', ')}`}
              {editReceipt.suggested > 0 && ` · ${editReceipt.suggested} asking in ◈`}
            </div>
          )}
          {draft !== null ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <textarea
                className="dw-input dw-textarea"
                rows={14}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" variant="primary" disabled={busy || !draft.trim()} onClick={saveEdit}>
                  {busy ? 'Saving…' : 'Save'}
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => setDraft(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="dw-reader-body">{renderAnswer(reading.content, byName, pickNode)}</div>
          )}
        </Card>
      )}

      <div className="dw-rail-section-title" style={{ marginBottom: 10 }}>Library</div>
      {docsLoading ? (
        <div className="dw-empty">Opening the trove…</div>
      ) : docsError ? (
        <div className="dw-error">The trove didn't answer — retry shortly.</div>
      ) : (docs ?? []).length === 0 ? (
        <div className="dw-rail-empty">Nothing here yet — import a note or capture one above.</div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {(docs ?? []).map((d) => (
            <button key={d.id} type="button" className="dw-queue-row" onClick={() => open(d)}>
              <span className="dw-queue-glyph">✧</span>
              <span className="dw-queue-title">
                {d.title ?? d.filename ?? 'untitled'}
                <span className="dw-inbox-detail">
                  {d.source} · {new Date(d.created_at).toLocaleDateString()}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
